
import { FunctionComponent, useEffect, useRef, useState } from 'react';
import { AutocompleteFieldTypesRecord, FieldTypes, InputTypes, InputTypesFieldTypesRecord } from '../../configuration/fieldTypes';
import { inputMaskMap } from '../../utilities/inputMasks/module';
import { TransactionFieldProps } from './TransactionField';
import { shouldUseCabelasBranding } from '../../utilities/shouldUseCabelasBranding';
import { Icons } from '../../icons';
import styles from './InputField.module.scss';

const eyeconSVG = Icons.EyeCon;
const noEyeconSVG = Icons.NoEyeCon;
const cabelasEyeConSVG = Icons.CabelasEyeCon;
const cabelasNoEyeConSVG = Icons.CabelasNoEyeCon;


export const InputField: FunctionComponent<TransactionFieldProps> = ({
	field,
	className,
	elegantStyling,
	disabled,
	onBlur,
	defaultValue,
	readOnly,
	labelRef,
	showValidation,
	changeHandler,
	onKeyDown,
	useRefFunc = useRef
}) => {
	const [isEmpty, setIsEmpty] = useState<boolean>(!(defaultValue || field.GetValue()));
	const [forceTextType, setForceTextType] = useState<boolean>(false);
	const showEyeCon = field.Type === FieldTypes.Password;
	const inputRef = useRefFunc<HTMLInputElement>(null);
	let changeEventListenerAttached = false;
	const changeEvent = (event: React.ChangeEvent<HTMLInputElement>) => {
		changeHandler?.(event);
		const eventValue = event.target.value;
		field.SetValue(eventValue);
		setIsEmpty(!eventValue);
	};

	useEffect(() => {
		if (inputRef.current && !changeEventListenerAttached) {
			changeEventListenerAttached = true;
			inputRef.current.addEventListener('change', changeEvent as any);
		}
		return () => {
			inputRef.current?.removeEventListener('change', changeEvent as any);
		};
	}, [inputRef.current]);

	useEffect(() => {
		const fieldInputValueSyncInterval = setInterval(() => {
			if (inputRef.current && field.GetValue() !== inputRef.current.value) {
				inputRef.current.dispatchEvent(new Event('change'));
			}
		}, 200);

		const validitySubId = field.Validity.Subscribe(() => {
			const inputMask = inputMaskMap.get(field.Key);

			if (inputRef.current && inputMask) {
				const initialValue = inputRef.current.value;
				inputMask({ target: inputRef.current });
				const newValue = inputRef.current.value;

				if (initialValue !== newValue) {
					field.SetValue(newValue);
				}
			}
		});

		return () => {
			field.Validity.Cancel(validitySubId);
			clearInterval(fieldInputValueSyncInterval);
		};
	}, []);

	const useCabelasBranding = shouldUseCabelasBranding();

	return (
		<>
			<div

				className={`${styles.newWrapper} ${isEmpty ? styles.empty : ''} ${showValidation ? styles.invalid : ''} ${
					showEyeCon ? styles.password : ''
				} ${elegantStyling ? styles.elegant : ''}`}
			>
				<label htmlFor={field.Key} ref={labelRef}>
					{field.Label}
				</label>
				{/* eslint-disable-next-line enforce-ids-in-jsx/missing-ids */}
				<input
					ref={inputRef}
					id={field.Key}
					name={field.Key}
					type={forceTextType ? InputTypes.Text : InputTypesFieldTypesRecord[field.Type]}
					readOnly={readOnly}
					onKeyDown={onKeyDown}
					onChange={changeEvent}
					onFocus={() => setIsEmpty(false)}
					onBlur={(event) => {
						const eventValue = event.target.value;
						field.SetValue(eventValue);
						setIsEmpty(!eventValue);
						onBlur?.(event);
						if (elegantStyling) {
							field.Clean = false;
						}
					}}
					autoComplete={AutocompleteFieldTypesRecord[field.Type]}
					defaultValue={defaultValue || field.GetValue()}
					className={className}
					disabled={disabled}
					data-testid="InputFieldInput"
				/>
				{showEyeCon && (
					// eslint-disable-next-line enforce-ids-in-jsx/missing-ids
					<button
						id={`${field.Key}_toggleText`}
						onClick={(e) => {
							e.preventDefault();
							setForceTextType(!forceTextType);
						}}
						tabIndex={1}
						type="button"
					>
						{forceTextType ? (useCabelasBranding ? cabelasNoEyeConSVG : noEyeconSVG) : (useCabelasBranding ? cabelasEyeConSVG : eyeconSVG)}
					</button>
				)}
				{showValidation && <p className={styles.validation}>{field.ValidationMessage}</p>}
			</div>
		</>
	);
};
